// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type BaseQueryEnhancer } from '@reduxjs/toolkit/query';

/** Options for configuring the delay query. */
export interface DelayQueryOptions {
  /** The delay duration in milliseconds. */
  delay?: number;
}

/**
 * Enhances a base query to include an optional delay before the fetch request.
 *
 * @remarks
 * This is useful only for development and testing, to simulate slow speed
 * connections.
 * @param baseQuery - The base query function to enhance.
 * @param config - Default configuration options for the delay query.
 * @returns A function that executes the enhanced query with the specified delay.
 */
export const delayQuery: BaseQueryEnhancer<
  unknown,
  DelayQueryOptions,
  void | DelayQueryOptions
> =
  (baseQuery, config) =>
  async (args, api, extraOptions): Promise<any> => {
    const configDelay = extraOptions?.delay ?? config?.delay;

    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    if (configDelay) {
      await delay(configDelay);
    }

    return baseQuery(args, api, extraOptions);
  };
